import { V, V2d } from "../Vector";

/**
 * Generate a regular n-sided polygon centered at origin
 * @param sides Number of sides
 * @param radius Distance from center to vertices
 * @param rotationOffset Optional rotation in radians
 * @returns Array of points defining the polygon
 */
export function createRegularPolygon(
  sides: number,
  radius: number,
  rotationOffset: number = 0,
): V2d[] {
  const points: V2d[] = [];

  for (let i = 0; i < sides; i++) {
    const angle = (i / sides) * Math.PI * 2 + rotationOffset;
    const x = Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius;
    points.push(V(x, y));
  }

  return points;
}

export interface PolygonShape {
  points: V2d[];
  color: number;
  secondaryColor?: number;
  strokeWidth?: number;
}

export interface LineSegment {
  start: V2d;
  end: V2d;
  center: V2d;
  angle: number;
  length: number;
}

/**
 * Scale a polygon by a given factor
 * @param points Polygon vertices
 * @param scaleX Scaling factor for X axis
 * @param scaleY Scaling factor for Y axis
 * @returns New array of scaled points
 */
export function scalePolygon(
  points: V2d[],
  scaleX: number,
  scaleY: number = scaleX,
): V2d[] {
  return points.map((point) => point.mulComponent([scaleX, scaleY]));
}

/**
 * Rotate a polygon by a given angle
 * @param points Polygon vertices
 * @param angle Rotation angle in radians
 * @returns New array of rotated points
 */
export function rotatePolygon(points: V2d[], angle: number): V2d[] {
  return points.map((point) => point.rotate(angle));
}

/**
 * Extract line segments from a polygon defined by points
 */
export function extractLineSegments(points: V2d[]): LineSegment[] {
  const segments: LineSegment[] = [];

  // Extract all segments including the closing one
  for (let i = 0; i < points.length; i++) {
    const start = points[i];
    const end = points[(i + 1) % points.length]; // Wrap to first point for last segment

    const center = start.add(end).imul(0.5);
    const diff = end.sub(start);
    const length = diff.magnitude;
    const angle = diff.angle;

    segments.push({
      start,
      end,
      center,
      angle,
      length,
    });
  }

  return segments;
}

/**
 * Calculate the center point of a polygon
 */
export function calculatePolygonCenter(points: V2d[]): V2d {
  if (points.length === 0) return V(0, 0);

  let center = V(0, 0);
  for (const point of points) {
    center = center.add(point);
  }
  return center.imul(1 / points.length);
}

/**
 * Calculate outward direction for a line segment based on polygon center
 */
export function calculateOutwardDirection(
  segment: LineSegment,
  polygonCenter: V2d = V(0, 0),
): V2d {
  // Vector from polygon center to segment center
  const toSegment = segment.center.sub(polygonCenter);

  // If segment is at center, use perpendicular to segment
  if (toSegment.magnitude < 0.001) {
    return V(-Math.sin(segment.angle), Math.cos(segment.angle));
  }

  return toSegment.normalize();
}
